<template>

  <div :class="$style.mainSidebar">
    <vue-custom-scrollbar class="scroll-area sidebar-scroll-area"
                          :settings="settings">
      <div v-if="showAdminInfo"
           :class="$style.goBackLinkWrapper">
        <router-link :class="$style.goBackLink" to="/admin">Admin Portal</router-link>
      </div>
      <div :class="$style.sidebarLogo">
        <app-logo v-bind:class="{ logo: showAdminInfo}"/>
      </div>

      <app-dropdown color="#102E52"
                    box-shadow="0 2px 4px 0 #091E37"
                    v-if="!showAdminInfo">
        <template v-slot:dropdownTitle>
          <span :class="$style.userName">{{ getUsername }}</span>
        </template>
        <template v-slot:dropdownContent>
          <div :class="$style.dropdownButton"
               @click="goToPurchases">
            My Purchases
          </div>
          <div :class="$style.dropdownLinkWrapper">
            <router-link :class="$style.dropdownLink" to="/logout">Logout</router-link>
          </div>
          <div v-if="isDevelopment"
               :class="$style.dropdownButton"
               @click="unlock">
            Unlock
          </div>
        </template>
      </app-dropdown>

    <div :class="$style.formProgress">
      <div :class="$style.formCompletion">
        <div :class="$style.formCompletionText">Form Completed</div>
        <div :class="$style.formCompletionNumbers">
          <span :class="$style.formCompletionAmount">{{ formProgress }}</span>
          <span :class="$style.formCompletionPercentage">%</span>
        </div>
      </div>
      <v-progress-linear height="12"
                         background-color="#385270"
                         color="#DBB13C"
                         v-model="formProgress"
      ></v-progress-linear>
    </div>

    <ul :class="$style.navList">
      <li v-for="(item, index) of getMenuItems"
          :key="index"
          :class="$style.navListItem">
        <router-link v-show="isShowItem(item)"
                     :to="{
                       params: {
                       id: getCPAId,
                       },
                       name: item.link,
                      }"
                     :active-class="$style.active"
                     :class="[$style.navListItemLink,
                     {disabled: getDisabled(item, index)}]">
          <div :class="$style.navListItemIcon">
            <app-svg-icon :class="$style.menuIcon"
                          :name="item.icon" />
          </div>
          <div :class="$style.navListItemName">
            {{ item.title }}
          </div>
          <div :class="$style.navListItemCheck">
            <app-svg-icon v-if="item.completed"
                          :class="$style.checkIcon"
                          name="ic_check" />
          </div>
        </router-link>
      </li>
    </ul>
    </vue-custom-scrollbar>
    <div :class="$style.helpSection">
      <template v-if="!showAdminInfo">
        <p :class="$style.helpQuestion">
          <span :class="$style.questionMark">?</span>
          Need help?
        </p>
        <app-button button-style="accent"
                    :class="$style.coachingBtn"
                    :disabled="buttonDisabled"
                    text="Buy Coaching"
                    @click="buyCoaching"/>
      </template>

      <div :class="$style.customerId">
        Customer ID: {{ currentYearUserApplicationId }}
      </div>
      <div :class="$style.policy">
        <a @click="openTermsAndService">Terms &amp; Conditions</a> |
        <a @click="openPrivacyPolicy">Privacy Policy</a>
      </div>

    </div>
  </div>

</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import VueCustomScrollbar from 'vue-custom-scrollbar';
import { USER_ACCESS_TYPES } from '@/constants';

export default {
  components: {
    VueCustomScrollbar,
  },
  data() {
    return {
      settings: {
        wheelSpeed: 0.5,
      },
      buttonDisabled: false,
    };
  },
  methods: {
    ...mapActions([
      'unlockSections',
    ]),
    getDisabled(item, index) {
      let isPreviousSectionNotCompleted = index > 0 && !this.getMenuItems[index - 1].completed;
      if (item.name === 'expenses' && this.getAppAccessType !== USER_ACCESS_TYPES.Business) {
        isPreviousSectionNotCompleted = index > 0 && !this.getMenuItems[index - 2].completed;
      }
      return isPreviousSectionNotCompleted || !this.getIsAppAccessPaid;
    },
    isShowItem(item) {
      return !(item.name === 'business' && this.getAppAccessType !== USER_ACCESS_TYPES.Business);
    },
    unlock() {
      this.unlockSections(this.getUserId);
    },
    buyCoaching() {
      if (this.$route.name !== 'buy-coaching') {
        this.buttonDisabled = true;
        this.$router.push({ name: 'buy-coaching' }, () => {
          this.buttonDisabled = false;
        });
      }
    },
    goToPurchases() {
      if (this.$route.name !== 'purchases') {
        this.$router.push({ name: 'purchases' });
      }
    },
    openTermsAndService() {
      this.$emit('openTermsAndService');
    },
    openPrivacyPolicy() {
      this.$emit('openPrivacyPolicy');
    },
  },
  computed: {
    ...mapGetters([
      'getMenuItems',
      'getFormProgress',
      'getUsername',
      'getSectionsLoadingStatus',
      'getQualifyStatus',
      'getUserId',
      'getIsAppAccessPaid',
      'getAppAccessType',
      'getCurrentYearUserApplicationId',
      'getCPAId',
      'getIsCPA',
    ]),
    isDevelopment() {
      return process.env.NODE_ENV === 'development';
    },
    formProgress() {
      return this.getFormProgress.formProgress;
    },
    currentYearUserApplicationId() {
      return this.getCurrentYearUserApplicationId;
    },
    showAdminInfo() {
      return this.getIsCPA;
    },
  },
};
</script>

<style lang="scss" module>
@import "@/styles/variables.scss";

.mainSidebar {
  width: 250px;
  min-width: 250px;
  height: 100%;
  background-color: $left-sidebar-color;
  color: $white-color;
  position: fixed;
  left: 0;
  top: 0;
  overflow-y: auto;
  z-index: 100;
  max-height: 100vh;

  .goBackLinkWrapper {
    text-align: center;
    .goBackLink {
      display: inline-block;
      margin: 20px 0 0 0;
      width: 100%;
      height: 100%;
      text-decoration: none;
      color: $white-color;
    }
  }

  .formProgress {
    padding: 6px 24px 12px;
    border-top: $sidebar-border;
    border-bottom: $sidebar-border;
  }

  .navList {
    padding-left: 0;
    margin-bottom: 0;
    list-style: none;

    .navListItem {
      width: 100%;
      &::before {
        content: none;
      }
    }
  }
}

.formCompletion {
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;

  &Text {
    font-family: $title-font;
    color: $white-color;
    opacity: 0.5;
    font-size: 1.3rem;
    line-height: 1.5rem;
    font-style: italic;
  }

  &Amount {
    font-family: $title-font;
    font-style: italic;
    font-size: 3.2rem;
    line-height: 3rem;
  }

  &Percentage {
    font-family: $link-font;
    font-size: 2.4rem;
    font-weight: 700;
  }
}

.navListItemLink {
  display: flex;
  align-items: center;
  padding: 8px 24px;
  padding-left: 20px;
  border-left: 4px solid $left-sidebar-color;
  text-decoration: none;
  transition: all 0.25s ease-in-out;

  .navListItemName {
    font-family: $title-font;
    font-size: 1.8rem;
    flex-grow: 1;
    color: $white-color;
    padding-left: 24px;
    opacity: 0.5;
    transition: all 0.25s ease-in-out;
  }

  .menuIcon,
  .checkIcon {
    width: 24px;
    height: 24px;
  }

  .checkIcon {
    width: 18px;
    height: 18px;
  }

  .menuIcon {
    opacity: 0.5;
    transition: all 0.25s ease-in-out;
  }

  &.active {
    background-color: $left-sidebar-active-color;
    border-top: 1px solid rgba($white-color, 0.5);
    border-bottom: 1px solid rgba($white-color, 0.5);
    border-left: 4px solid $accent-color;

    .navListItemName,
    .menuIcon {
      opacity: 1;
    }
  }
}

.dropdownButton:not(:last-child) {
  margin-bottom: 20px;
}

.helpSection {
  padding: 14px 24px 0;
  text-align: center;
}

.helpQuestion {
  font-size: 1.3rem;
  font-style: italic;
  color: rgba($white-color, 0.55);

  .questionMark {
    display: inline-block;
    width: 18px;
    height: 18px;
    line-height: 17px;
    text-align: center;
    font-style: normal;
    font-weight: 700;
    border-radius: 50px;
    color: $main-text-color;
    background-color: $accent-color;
    margin-right: 6px;
  }
}

.coachingBtn {
  padding: 10px;
  width: 100%;
  margin-bottom: 20px;
}

.customerId {
  text-align: center;
  margin-bottom: 10px;
}

.dropdownLinkWrapper .dropdownLink {
  display: inline-block;
  margin-bottom: 20px;
  width: 100%;
  height: 100%;
  text-decoration: none;
  color: $white-color;
}

.policy {
  font-size: 1.3rem;
  color: $sidebar-link-color;
  margin: 0 -10px;
}
</style>

<style lang="scss">
  @import "@/styles/variables.scss";

  .logo {
    padding-top: 0;
  }

  .scroll-area {
    width: 100%;
    height: 100%;
    max-height: 100%;

    &.sidebar-scroll-area {
      border-bottom: $sidebar-border;
      /* Height is 100% minus height of the buy-button section */
      height: calc(100% - 202px);
    }
  }

  .ps:hover > .ps__rail-y {
    opacity: 1;
  }
  .ps__thumb-x, .ps__thumb-y,
  .ps__rail-y.ps--clicking .ps__thumb-y,
  .ps__rail-y:focus > .ps__thumb-y,
  .ps__rail-y:hover > .ps__thumb-y {
    background-color: $radio-active-color;
  }

  .disabled:not(.router-link-active) {
    cursor: pointer;
    pointer-events: none;
    opacity: 0.4;
  }

</style>
